import { useInsertionEffect } from 'react';
import { createFoundationStyles } from '../createFoundation/index.js';
import { GilroyFonts } from '../../../fonts/fonts/index.js';

/**
 * Custom React hook to manage and inject foundation styles, including fonts, themes, and writing modes.
 *
 * This hook is designed for React applications to dynamically apply global styles based on the provided configurations.
 * It utilizes `useEffect` to handle side effects related to style injection and ensures that styles are applied
 * when the component mounts and cleaned up when the component unmounts.
 *
 * @param {Object} params - Configuration parameters for foundation styles.
 * @param {Array} params.fonts - Array of font definitions to be applied. Defaults to `GilroyFonts`.
 * @param {Object} params.themes - Theme configurations to be applied.
 * @param {string} params.writingMode - Writing mode direction, either 'ltr' (left-to-right) or 'rtl' (right-to-left). Defaults to 'ltr'.
 *
 * @example
 * useFoundation({
 *   fonts: customFonts,
 *   themes: customThemes,
 *   writingMode: 'rtl',
 * });
 *
 * @returns {void|Function} - Returns a cleanup function if necessary, otherwise undefined.
 */
const useFoundation = ({ fonts = GilroyFonts, themes, writingMode = 'ltr', }) => {
    useInsertionEffect(() => {
        const result = createFoundationStyles({ fonts, themes, writingMode });
        return () => {
            if (typeof result === 'function') {
                result();
            }
        };
    }, [fonts, themes, writingMode]);
};

export { useFoundation };
